import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DateRangeType } from "react-tailwindcss-datepicker";

import { createColumnHelper, RowModel, Table } from "@tanstack/react-table";

import BulkListModal from "./BulkListModal";
import { IPartCategory } from '../../../models/Part';
import { PageMode } from "../../../models/PagedTable";
import VehiclePagedTable from "../../shared/Tables/VehiclePagedTable";
import { IPart } from "../../../models/Part";
import usePartMutations from "../../../data/usePartMutations";
import useNotification from "../../notifications/useNotifications";
import UnlistedExpandedRowContent from "./UnlistedExpandedRowContent";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import ChangeCategoryModal from "../../stock/stockFunctions/ChangeCatgeoryModal";
import ConfirmModal from "../../shared/Confirm/ConfirmModal";
import WarehouseLocationSearch from "../../warehouseLocations/WarehouseLocationSearch";
import useTemplateProductDescription from "../../../data/useTemplateProductDescription";
import usePartDefaultCategoriesMutations from "../../../data/usePartDefaultCategoriesMutations";
import DateRangeFilter from "../../shared/DateRangeFilter";
import { useForm } from "react-hook-form";
import { FormatType, ListingStatusEnum } from "../../../models/EbayOffer";
import { IWarehouseLocation } from "../../../models/Location";
import { useQueryClient } from "@tanstack/react-query";
import useWarehouseLocations from "../../../data/useWarehouseLocations";
import TableCheckboxCol from "../../shared/Tables/IndeterminateCheckbox";
import useCategoriesPartial from "../../../data/useCategoriesPartial";
import TriToggleInput, { type TriState } from "../../shared/Inputs/TriToggleInput";
import TableFilterPanel from "../../shared/Tables/TableFilterPanel";
import VehiclePartSearchFilters, {
  buildVehiclePartSearchBody,
  defaultVehicleDetailFilterEnabled,
  IVehiclePartSearchFields,
  VehicleDetailFilterKey,
  vehiclePartSearchDefaultValues,
} from "../../stock/VehiclePartSearchFilters";
import classNames from "../../../utils/classNames";
import toIsoDate from "../../../utils/toIsoDate";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import useBatchListingsMutations from "../../../data/useBatchListingsMutations";
import UnlistedImageCell from "./UnlistedImageCell";
import UnlistedPartTypeCell from "./UnlistedPartTypeCell";
import UnlistedDonorVehicleCell from "./UnlistedDonorVehicleCell";
import UnlistedPriceStockCell from "./UnlistedPriceStockCell";
import UnlistedListingWorkflowCell from "./UnlistedListingWorkflowCell";
import UnlistedEbayTitle from "./UnlistedEbayTitle";
import useBulkAiTitles from "../../../hooks/useBulkAiTitles";
import useSaveEbayOfferTitle from "../../../data/useSaveEbayOfferTitle";
import runWithConcurrency from "../../../utils/runWithConcurrency";

type LocationSearchMode = "equals" | "beginsWith";

interface IFormFields extends IVehiclePartSearchFields {
  partCategory: IPartCategory[];
  listingStatus: ListingStatusEnum;
  locationSearchMode: LocationSearchMode;
  warehouseLocation: IWarehouseLocation | undefined;
  warehouseLocationId: string | undefined;
  warehouseLocationBeginsWith: string;
  dateFieldType: string | undefined;
  statusMissingDeleted: TriState;
  statusQc: TriState;
  statusDismantled: TriState;
  hasLocation: TriState;
  hasImages: TriState;
  hasPrice: TriState;
  hasMpn: TriState;
  hasOeMpn: TriState;
  readyToList: TriState;
  tooManyImages: TriState;
};

interface IUnlistedSavedDefaultFilters {
  partCategory: IPartCategory[];
  listingStatus: ListingStatusEnum;
  locationSearchMode: LocationSearchMode;
  warehouseLocation: IWarehouseLocation | undefined;
  warehouseLocationBeginsWith: string;
  startDate: string | null;
  endDate: string | null;
  dateFieldType: string;
  statusMissingDeleted: TriState;
  statusQc: TriState;
  statusDismantled: TriState;
  hasLocation: TriState;
  hasImages: TriState;
  hasPrice: TriState;
  hasMpn: TriState;
  hasOeMpn: TriState;
  readyToList: TriState;
  tooManyImages: TriState;
}

const defaultFormValues: IFormFields = {
  ...vehiclePartSearchDefaultValues,
  partCategory: [],
  listingStatus: ListingStatusEnum["All"],
  locationSearchMode: "equals",
  warehouseLocation: undefined,
  warehouseLocationId: undefined,
  warehouseLocationBeginsWith: "",
  dateFieldType: "Created",
  statusMissingDeleted: "any",
  statusQc: "any",
  statusDismantled: "any",
  hasLocation: "any",
  hasImages: "with",
  hasPrice: "any",
  hasMpn: "any",
  hasOeMpn: "any",
  readyToList: "any",
  tooManyImages: "any",
};

function buildUnlistedTriStateFlags(state: {
  statusMissingDeleted: TriState | undefined;
  statusQc: TriState | undefined;
  statusDismantled: TriState | undefined;
  hasLocation: TriState | undefined;
  hasImages: TriState | undefined;
  hasPrice: TriState | undefined;
  hasMpn: TriState | undefined;
  hasOeMpn: TriState | undefined;
  readyToList: TriState | undefined;
  tooManyImages: TriState | undefined;
}): Record<string, true | undefined> {
  const map = (value: TriState | undefined, withKey: string, withoutKey: string): Record<string, true> => {
    if (value === "with") return { [withKey]: true };
    if (value === "without") return { [withoutKey]: true };
    return {};
  };

  return {
    ...map(state.statusMissingDeleted, "partsWithMissingOrDeletedStatus", "partsWithoutMissingOrDeletedStatus"),
    ...map(state.statusQc, "partsWithQcStatus", "partsWithoutQcStatus"),
    ...map(state.statusDismantled, "partsWithDismantledStatus", "partsWithoutDismantledStatus"),
    ...map(state.hasLocation, "partsWithLocation", "partsWithoutLocations"),
    ...map(state.hasImages, "partsWithImages", "partsWithoutImages"),
    ...map(state.hasPrice, "partsWithPrice", "partsWithoutPrice"),
    ...map(state.hasMpn, "partsWithMPN", "partsWithoutMPN"),
    ...map(state.hasOeMpn, "partsWithOeMPN", "partsWithoutOeMPN"),
    ...map(state.readyToList, "partsReadyToList", "partsNotReadyToList"),
    ...map(state.tooManyImages, "partsWithTooManyImages", "partsWithoutTooManyImages"),
  };
}

export default function EbayUnlistedTable() {
  const { autoReorderImages, scrapOutPart, sendToScrapOut } = usePartMutations();
  const { useBulkDefaultPartCategory } = usePartDefaultCategoriesMutations();
  const { bulkUpdateProductDescription } = useTemplateProductDescription();
  const { warehouseLocations } = useWarehouseLocations();
  const { categories: allCategories } = useCategoriesPartial();
  const { saveHandlers: bulkSaveHandlers } = useDefaultCRUDHandlers("Parts");
  const { addNotification } = useNotification();
  const { bulkPublish } = useBatchListingsMutations("EbayUnlistedTable");
  const bulkAiTitles = useBulkAiTitles();
  const { saveTitleAsync } = useSaveEbayOfferTitle();
  const queryClient = useQueryClient();
  const { register, setValue, getValues, watch, reset } = useForm<IFormFields>({
    defaultValues: defaultFormValues,
  });

  const bulkListLoading = false;
  const [toUpdate, setToUpdate] = useState<RowModel<IPart> | undefined>(undefined);
  const [updateCategoryOpen, setUpdateCategoryOpen] = useState<boolean>(false);
  const [bulkListModalOpen, setBulkListModalOpen] = useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [confirmFunction, setConfirmFunction] = useState<(() => void) | null>(null);
  const [confirmMessage, setConfirmMessage] = useState<string>("");
  const [selectedPartsForBulkList, setSelectedPartsForBulkList] = useState<IPart[] | undefined>(undefined);
  const tableRef = useRef<Table<IPart> | null>(null);
  const [dates, setDates] = useState<DateRangeType>({
    startDate: null,
    endDate: null
  });
  const [dateFieldType, setDateFieldType] = useState<string>("Created");
  const [additionalBody, setAdditionalBody] = useState<Record<string, any>>({});
  const [isSearching, setIsSearching] = useState(false);
  const [isSmartMatch, setIsSmartMatch] = useState(false);
  const [vehicleDetailFilterEnabled, setVehicleDetailFilterEnabled] = useState<Record<VehicleDetailFilterKey, boolean>>(defaultVehicleDetailFilterEnabled);

  const watchMake = watch("make");
  const watchModel = watch("model");
  const watchTrim = watch("trim");
  const watchYearFrom = watch("yearFrom");
  const watchYearTo = watch("yearTo");
  const watchBodyStyle = watch("bodyStyle");
  const watchNumberOfDoors = watch("numberOfDoors");
  const watchFuelType = watch("fuelType");
  const watchEngineSize = watch("engineSize");
  const watchEngineCode = watch("engineCode");
  const watchTransmission = watch("transmissionType");
  const watchPartNumber = watch("partNumber");
  const watchTagNumber = watch("tagNumber");
  const watchVrm = watch("vrm");
  const watchPartCategory = watch("partCategory");
  const watchListingStatus = watch("listingStatus");
  const watchWarehouseLocation = watch("warehouseLocation");
  const watchLocationSearchMode = watch("locationSearchMode");
  const watchWarehouseLocationBeginsWith = watch("warehouseLocationBeginsWith");
  const watchStatusMissingDeleted = watch("statusMissingDeleted");
  const watchStatusQc = watch("statusQc");
  const watchStatusDismantled = watch("statusDismantled");
  const watchHasLocation = watch("hasLocation");
  const watchHasImages = watch("hasImages");
  const watchHasPrice = watch("hasPrice");
  const watchHasMpn = watch("hasMpn");
  const watchHasOeMpn = watch("hasOeMpn");
  const watchReadyToList = watch("readyToList");
  const watchTooManyImages = watch("tooManyImages");

  // const ebayListingOptions = Object.keys(ListingStatusEnum)
  //   .filter(key => isNaN(Number(key)))
  //   .map((key, index) => ({
  //     id: index,
  //     name: key,
  //   }));

  const colHelper = createColumnHelper<IPart>();

  const cols = useMemo(
    () => [
      TableCheckboxCol(true, true, true, "ebay-listings", true),
      colHelper.display({
        id: "images",
        meta: {
          thClassName: "min-w-[12rem] w-52 !p-1.5",
          tdClassName: "min-w-[12rem] w-52 !p-1.5 align-top h-px",
        },
        cell: (info) => <UnlistedImageCell part={info.row.original} />,
      }),
      colHelper.accessor("categoryId", {
        header: "Part information",
        id: "categoryId",
        sortDescFirst: false,
        sortingFn: "auto",
        meta: {
          thClassName: "min-w-[11rem] max-w-[14rem] w-[12rem] align-top !p-1.5",
          tdClassName: "min-w-[11rem] max-w-[14rem] w-[12rem] align-top h-px !p-1.5",
        },
        cell: (info) => (
          <UnlistedPartTypeCell
            part={info.row.original}
            categories={allCategories.data?.data}
          />
        ),
      }),
      colHelper.accessor("vehicle.vrm", {
        header: "Donor vehicle",
        id: "vrm",
        sortingFn: "auto",
        meta: {
          thClassName: "min-w-[10rem] max-w-[15rem] w-[11rem] align-top !p-1.5",
          tdClassName: "min-w-[10rem] max-w-[15rem] w-[11rem] align-top h-px !p-1.5",
        },
        cell: (info) => <UnlistedDonorVehicleCell part={info.row.original} />,
      }),
      colHelper.accessor(row => {
        const offer = row.offers?.[0];
        const offerPrice = offer
          ? (offer.format === FormatType.Auction ? offer.auctionStartPrice : offer.buyItNowPrice)
          : null;
        return offerPrice ?? row.enteredPrice ?? 0;
      }, {
        id: "offer.price",
        header: "Price & stock",
        meta: {
          thClassName: "min-w-[10rem] max-w-[13rem] w-[11rem] align-top !p-1.5",
          tdClassName: "min-w-[10rem] max-w-[13rem] w-[11rem] align-top h-px !p-1.5",
        },
        cell: (info) => <UnlistedPriceStockCell part={info.row.original} />,
      }),
      colHelper.display({
        id: "listing-workflow",
        header: "Listing workflow",
        enableSorting: false,
        meta: {
          thClassName: "w-[22rem] min-w-[20rem] max-w-[28rem] align-top !p-1.5",
          tdClassName: "w-[22rem] min-w-[20rem] max-w-[28rem] align-top h-px !p-1.5",
        },
        cell: (info) => (
          <div className="flex h-full" onClick={(e) => e.stopPropagation()}>
            <UnlistedListingWorkflowCell part={info.row.original} />
          </div>
        ),
      }),
    ],
    [allCategories.isLoading, allCategories.data]
  );



  const bulkList = (rows: RowModel<IPart>, table: Table<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    tableRef.current = table;
    setSelectedPartsForBulkList(rows.rows.map((row) => row.original));
    setBulkListModalOpen(true);
  };

  const handleBulkListComplete = () => {
    if (tableRef.current) {
      tableRef.current.toggleAllRowsSelected(false);
      tableRef.current = null;
    }
  };

  const handleAutoReorderImages = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    const ids = selected.map((row) => row.original.id);

    autoReorderImages.mutate(ids, bulkSaveHandlers);
  }

  const handleApplyBulkDefaults = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    const body = {
      partIds: selected.map((row) => row.original.id)
    };

    useBulkDefaultPartCategory.mutate(body as any, {
      ...bulkSaveHandlers,
      onSuccess: () => {
        bulkSaveHandlers.onSuccess();
        queryClient.invalidateQueries(["pagedTable", PageMode.Unlisted]);
      }
    });
  };


  const handleBulkResetDescription = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    const partIds = selected.map((row) => row.original.id);

    bulkUpdateProductDescription.mutate({ partIds }, bulkSaveHandlers);
  };


  // Unlisted parts aren't on eBay yet, so the AI title is just written onto each part's offer
  // (same as the per-row sparkle button) and goes out when the part is listed.
  const handleBulkAiTitles = async (rows: RowModel<IPart>) => {
    const parts = rows.rows
      .filter((row) => row.getIsSelected() && row.original.soldDate == null)
      .map((row) => row.original);

    if (parts.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    const { generated, failed } = await bulkAiTitles.generateTitles(parts);
    const saves = await runWithConcurrency(generated, 4, ({ part, title }) => saveTitleAsync(part, title));
    const saved = saves.filter((save) => save.ok).length;
    const notSaved = failed + (saves.length - saved);

    queryClient.invalidateQueries(["pagedTable", PageMode.Unlisted]);

    addNotification({
      primaryText: `AI titles saved for ${saved} part${saved === 1 ? "" : "s"}${notSaved > 0 ? `, ${notSaved} failed` : ""}`,
      variant: saved === 0 ? "error" : notSaved > 0 ? "warn" : "success",
    });
  };

  const handleOpenChangeCategory = (rows: RowModel<IPart>) => {
    setToUpdate(rows);
    setUpdateCategoryOpen(true);
  };

  const handleConfirm = (onConfirm: () => void, message: string) => {
    setConfirmFunction(() => onConfirm);
    setConfirmMessage(message);
    setConfirmOpen(true);
  };

  const handleFinishedConfirm = () => {
    setConfirmOpen(false);
    setConfirmFunction(null);
    setConfirmMessage("");
  };

  // Queues the parts for the yard app's Scrap Out list. Nothing is written off here - the app
  // lists them and an operator scans each tag to confirm before the part is actually scrapped.
  const handleBulkSendToScrapOut = async (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    try {
      await sendToScrapOut.mutateAsync(selected.map((row) => row.original.id));
      selected.forEach((row) => row.toggleSelected(false));
      bulkSaveHandlers.onSuccess();
    } catch {
      bulkSaveHandlers.onError();
    }
  };

  // deletedReason: 1 = scrap out (vs 0 = normal delete) - the API routes these differently
  const handleBulkScrap = async (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    try {
      await Promise.all(
        selected.map(async (row) => {
          await scrapOutPart.mutateAsync(row.original.id);
          row.toggleSelected(false);
        })
      );
      bulkSaveHandlers.onSuccess();
    } catch {
      bulkSaveHandlers.onError();
    }
  };

  const expandedRow = (part: IPart) => {
    return <UnlistedExpandedRowContent part={part} />;
  };

  const extraRow = (part: IPart) => {
    return <UnlistedEbayTitle part={part} />;
  };

  const handleDatesChange = (newDates: DateRangeType) => {
    setDates(newDates);
  };

  const handleClearForm = () => {
    reset();
    setIsSmartMatch(false);
    setDates({ startDate: null, endDate: null });
    setDateFieldType("Created");
    setAdditionalBody({});
    // setQueryInput("");
    // setAppliedQuery("");
  };

  useEffect(() => {
    const triFlags = buildUnlistedTriStateFlags({
      statusMissingDeleted: watchStatusMissingDeleted,
      statusQc: watchStatusQc,
      statusDismantled: watchStatusDismantled,
      hasLocation: watchHasLocation,
      hasImages: watchHasImages,
      hasPrice: watchHasPrice,
      hasMpn: watchHasMpn,
      hasOeMpn: watchHasOeMpn,
      readyToList: watchReadyToList,
      tooManyImages: watchTooManyImages,
    });

    const vehiclePartSearchBody = isSmartMatch
      ? {
        smartSearch: watchVrm,
        categoryIds: watchPartCategory?.map((category) => category.id),
      }
      : buildVehiclePartSearchBody(getValues(), vehicleDetailFilterEnabled);

    setAdditionalBody({
      ...vehiclePartSearchBody,
      listingStatus: watchListingStatus,
      locationSearchMode: watchLocationSearchMode,
      warehouseLocationId: watchLocationSearchMode === "equals" ? watchWarehouseLocation?.id : undefined,
      warehouseLocationBeginsWith: watchLocationSearchMode === "beginsWith" ? watchWarehouseLocationBeginsWith || undefined : undefined,
      startDate: dates.startDate ? toIsoDate(dates.startDate) : undefined,
      endDate: dates.endDate ? toIsoDate(dates.endDate) : undefined,
      dateFieldType: dateFieldType,
      ...(!isSmartMatch ? triFlags : {}),
    });

  }, [getValues, isSmartMatch, vehicleDetailFilterEnabled, watchMake, watchModel, watchTrim, watchYearFrom, watchYearTo, watchBodyStyle, watchNumberOfDoors, watchFuelType, watchEngineSize, watchEngineCode, watchTransmission, watchPartNumber, watchTagNumber, watchVrm, watchPartCategory, watchListingStatus, watchWarehouseLocation, watchLocationSearchMode, watchWarehouseLocationBeginsWith, dates, dateFieldType, watchStatusMissingDeleted, watchStatusQc, watchStatusDismantled, watchHasLocation, watchHasImages, watchHasPrice, watchHasMpn, watchHasOeMpn, watchReadyToList, watchTooManyImages]);

  // Monitor PagedTable query state to prevent crashes
  const [queryState, setQueryState] = useState<any>(null);

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      const cache = queryClient.getQueryCache();
      const pagedTableQueries = cache.getAll().filter((query) => (query.queryKey[0] === "pagedTable" || query.queryKey[0] === "vehiclePagedTable"));
      setQueryState(pagedTableQueries[pagedTableQueries.length - 1]?.state || null);
    });
    return () => unsubscribe();
  }, [queryClient]);

  const isSearchDisabled = useMemo(
    () => !queryState || queryState.status === "loading" || isSearching,
    [queryState, isSearching]
  );

  const activeFilterPills = useMemo(() => {
    const pills: { key: string; label: string; onClear: () => void }[] = [];
    const triLabel = (label: string, value: TriState | undefined) => value && value !== "any" ? `${label}: ${value === "with" ? "With" : "Without"}` : undefined;

    if (watchPartCategory?.length) {
      pills.push({
        key: "partCategory",
        label: `Categories: ${watchPartCategory.length}`,
        onClear: () => setValue("partCategory", [] as any),
      });
    }

    if (watchWarehouseLocation?.id || watchWarehouseLocationBeginsWith) {
      pills.push({
        key: "warehouseLocation",
        label: `Location: ${watchWarehouseLocation?.displayName ?? watchWarehouseLocationBeginsWith}`,
        onClear: () => {
          setValue("warehouseLocation", undefined as any);
          setValue("warehouseLocationId", undefined as any);
          setValue("warehouseLocationBeginsWith", "" as any);
        },
      });
    }

    if (dates.startDate || dates.endDate) {
      pills.push({
        key: "dates",
        label: `Dates: ${dateFieldType}`,
        onClear: () => setDates({ startDate: null, endDate: null }),
      });
    }

    [
      { key: "statusMissingDeleted", label: triLabel("Missing/Deleted", watchStatusMissingDeleted), clear: () => setValue("statusMissingDeleted", "any" as any) },
      { key: "statusQc", label: triLabel("QC", watchStatusQc), clear: () => setValue("statusQc", "any" as any) },
      { key: "statusDismantled", label: triLabel("Dismantled", watchStatusDismantled), clear: () => setValue("statusDismantled", "any" as any) },
      { key: "hasLocation", label: triLabel("Location", watchHasLocation), clear: () => setValue("hasLocation", "any" as any) },
      { key: "hasImages", label: triLabel("Images", watchHasImages), clear: () => setValue("hasImages", "any" as any) },
      { key: "hasPrice", label: triLabel("Price", watchHasPrice), clear: () => setValue("hasPrice", "any" as any) },
      { key: "hasMpn", label: triLabel("MPN", watchHasMpn), clear: () => setValue("hasMpn", "any" as any) },
      { key: "hasOeMpn", label: triLabel("OE MPN", watchHasOeMpn), clear: () => setValue("hasOeMpn", "any" as any) },
      { key: "readyToList", label: triLabel("Ready to list", watchReadyToList), clear: () => setValue("readyToList", "any" as any) },
      { key: "tooManyImages", label: triLabel("Too many images", watchTooManyImages), clear: () => setValue("tooManyImages", "any" as any) },
    ].forEach((pill) => {
      if (!pill.label) return;
      pills.push({ key: pill.key, label: pill.label, onClear: pill.clear });
    });

    return pills;
  }, [watchPartCategory, watchWarehouseLocation?.id, watchWarehouseLocation?.displayName, watchWarehouseLocationBeginsWith, dates.startDate, dates.endDate, dateFieldType, watchStatusMissingDeleted, watchStatusQc, watchStatusDismantled, watchHasLocation, watchHasImages, watchHasPrice, watchHasMpn, watchHasOeMpn, watchReadyToList, watchTooManyImages, setValue]);

  const getDefaultFilters = useCallback((): IUnlistedSavedDefaultFilters => ({
    partCategory: watchPartCategory,
    listingStatus: watchListingStatus,
    locationSearchMode: watchLocationSearchMode,
    warehouseLocation: watchWarehouseLocation,
    warehouseLocationBeginsWith: watchWarehouseLocationBeginsWith,
    startDate: dates.startDate ? toIsoDate(dates.startDate) : null,
    endDate: dates.endDate ? toIsoDate(dates.endDate) : null,
    dateFieldType,
    statusMissingDeleted: watchStatusMissingDeleted,
    statusQc: watchStatusQc,
    statusDismantled: watchStatusDismantled,
    hasLocation: watchHasLocation,
    hasImages: watchHasImages,
    hasPrice: watchHasPrice,
    hasMpn: watchHasMpn,
    hasOeMpn: watchHasOeMpn,
    readyToList: watchReadyToList,
    tooManyImages: watchTooManyImages,
  }), [watchPartCategory, watchListingStatus, watchLocationSearchMode, watchWarehouseLocation, watchWarehouseLocationBeginsWith, dates.startDate, dates.endDate, dateFieldType, watchStatusMissingDeleted, watchStatusQc, watchStatusDismantled, watchHasLocation, watchHasImages, watchHasPrice, watchHasMpn, watchHasOeMpn, watchReadyToList, watchTooManyImages]);

  const applyDefaultFilters = useCallback((savedFilters: IUnlistedSavedDefaultFilters) => {
    setValue("partCategory", savedFilters.partCategory as any);
    setValue("listingStatus", savedFilters.listingStatus as any);
    setValue("locationSearchMode", (savedFilters.locationSearchMode ?? "equals") as any);
    setValue("warehouseLocation", savedFilters.warehouseLocation as any);
    setValue("warehouseLocationId", savedFilters.warehouseLocation?.id as any);
    setValue("warehouseLocationBeginsWith", (savedFilters.warehouseLocationBeginsWith ?? "") as any);
    setValue("statusMissingDeleted", (savedFilters.statusMissingDeleted ?? "any") as any);
    setValue("statusQc", (savedFilters.statusQc ?? "any") as any);
    setValue("statusDismantled", (savedFilters.statusDismantled ?? "any") as any);
    setValue("hasLocation", (savedFilters.hasLocation ?? "any") as any);
    setValue("hasImages", (savedFilters.hasImages ?? "any") as any);
    setValue("hasPrice", (savedFilters.hasPrice ?? "any") as any);
    setValue("hasMpn", (savedFilters.hasMpn ?? "any") as any);
    setValue("hasOeMpn", (savedFilters.hasOeMpn ?? "any") as any);
    setValue("readyToList", (savedFilters.readyToList ?? "any") as any);
    setValue("tooManyImages", (savedFilters.tooManyImages ?? "any") as any);

    setDates({
      startDate: savedFilters.startDate ? new Date(savedFilters.startDate) : null,
      endDate: savedFilters.endDate ? new Date(savedFilters.endDate) : null,
    });
    setDateFieldType(savedFilters.dateFieldType ?? "Created");
  }, [setValue]);


  return (
    <>
      <ConfirmModal open={confirmOpen} setOpen={setConfirmOpen}
        title={"Confirm changes to parts?"}
        message={confirmMessage}
        confirmButtonText={"Confirm"}
        onConfirm={() => { confirmFunction?.(); handleFinishedConfirm(); }}
        isLoading={false}
      />

      <ChangeCategoryModal open={updateCategoryOpen} setOpen={setUpdateCategoryOpen} setToUpdate={setToUpdate} parts={toUpdate?.rows.map((row) => row.original)!} />
      {selectedPartsForBulkList && (
        <BulkListModal
          open={bulkListModalOpen}
          setOpen={setBulkListModalOpen}
          selectedParts={selectedPartsForBulkList}
          onComplete={handleBulkListComplete}
        />
      )}

      <form className="my-2 mb-4 space-y-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <VehiclePartSearchFilters
          register={register}
          setValue={setValue}
          getValues={getValues}
          watch={watch}
          categories={allCategories.data?.data ?? []}
          categoriesLoading={allCategories.isLoading}
          disabled={isSearchDisabled}
          isSearching={isSearching}
          setIsSearching={setIsSearching}
          isSmartMatch={isSmartMatch}
          setIsSmartMatch={setIsSmartMatch}
          vehicleDetailFilterEnabled={vehicleDetailFilterEnabled}
          setVehicleDetailFilterEnabled={setVehicleDetailFilterEnabled}
          onApplySmartSearch={setAdditionalBody}
          getAdditionalSmartSearchBody={(data) => ({ listingStatus: data.listingStatus })}
        />

        <hr className="mt-1" />

        <TableFilterPanel
          activeFilterPills={activeFilterPills}
          disabled={isSearchDisabled}
          defaultFiltersStorageKey="wilma:tableFilters:unlisted"
          getDefaultFilters={getDefaultFilters}
          onApplyDefaultFilters={applyDefaultFilters}
        >
          <div className="flex flex-wrap items-end gap-x-4 gap-y-2">

            <TriToggleInput label="Missing/Deleted" value={watchStatusMissingDeleted} onChange={(value) => setValue("statusMissingDeleted", value as any)} disabled={isSearchDisabled} />
            <TriToggleInput label="QC" value={watchStatusQc} onChange={(value) => setValue("statusQc", value as any)} disabled={isSearchDisabled} />
            <TriToggleInput label="Dismantled" value={watchStatusDismantled} onChange={(value) => setValue("statusDismantled", value as any)} disabled={isSearchDisabled} />
            <TriToggleInput label="Location" value={watchHasLocation} onChange={(value) => setValue("hasLocation", value as any)} disabled={isSearchDisabled} />
            <TriToggleInput label="Images" value={watchHasImages} onChange={(value) => setValue("hasImages", value as any)} disabled={isSearchDisabled} />
            <TriToggleInput label="Price" value={watchHasPrice} onChange={(value) => setValue("hasPrice", value as any)} disabled={isSearchDisabled} />
            <TriToggleInput label="MPN" value={watchHasMpn} onChange={(value) => setValue("hasMpn", value as any)} disabled={isSearchDisabled} />
            <TriToggleInput label="OE MPN" value={watchHasOeMpn} onChange={(value) => setValue("hasOeMpn", value as any)} disabled={isSearchDisabled} />
            <TriToggleInput label="Ready to list" value={watchReadyToList} onChange={(value) => setValue("readyToList", value as any)} disabled={isSearchDisabled} />
            <TriToggleInput label="Too many images" value={watchTooManyImages} onChange={(value) => setValue("tooManyImages", value as any)} disabled={isSearchDisabled} />

            <WarehouseLocationSearch
              showSearchOptions={true}
              register={register}
              setValue={setValue}
              watch={watch}
              isSearchDisabled={isSearchDisabled}
              warehouseLocations={warehouseLocations}
            />

            <DateRangeFilter
              dates={dates}
              onDatesChange={handleDatesChange}
              dateFieldType={dateFieldType}
              onDateFieldTypeChange={setDateFieldType}
              disabled={isSearchDisabled}
            />
          </div>
        </TableFilterPanel>

        <ButtonInput
          label="Clear"
          classes={classNames(isSearchDisabled ? "" : "!bg-white !text-gray-700 !border !border-gray-300 hover:!bg-gray-100")}
          onClick={handleClearForm}
          disabled={isSearchDisabled}
        />
      </form>


      <VehiclePagedTable
        url="/part/listings"
        pageMode={PageMode.Unlisted}
        columns={cols}
        queryLabel="Search Part Details"
        customUrlQuery=""
        useButtonGroup
        actionButtons={[
          { label: "Bulk List", onClick: bulkList, isLoading: bulkListLoading, disabledOnNoRows: true },
          { label: "Auto Reorder Images", onClick: handleAutoReorderImages, isLoading: autoReorderImages.isLoading, disabledOnNoRows: true },
          { label: "Apply Defaults", onClick: handleApplyBulkDefaults, isLoading: useBulkDefaultPartCategory.isLoading, disabledOnNoRows: true },
          { label: "Reset Description", onClick: handleBulkResetDescription, isLoading: bulkUpdateProductDescription.isLoading, disabledOnNoRows: true },
          { label: bulkAiTitles.label, onClick: (rows: RowModel<IPart>) => handleConfirm(() => handleBulkAiTitles(rows), "Generate an AI title for each selected part?\nThis replaces their current eBay titles."), isLoading: bulkAiTitles.isGenerating, disabledOnNoRows: true },
          { label: "Change Category", onClick: handleOpenChangeCategory, disabledOnNoRows: true },
          { label: "Send to Scrap Out", onClick: (rows: RowModel<IPart>) => handleConfirm(() => handleBulkSendToScrapOut(rows), "Send the selected parts to the app's Scrap Out list?\nNothing is scrapped yet - a yard operator scans each tag in the app to confirm."), isLoading: sendToScrapOut.isLoading, disabledOnNoRows: true },
          { label: "Scrap", onClick: (rows: RowModel<IPart>) => handleConfirm(() => handleBulkScrap(rows), "Are you sure you want to scrap the selected parts?\nThis marks them as scrapped and removes them from stock."), isLoading: scrapOutPart.isLoading, disabledOnNoRows: true },
        ]}
        expandedRow={expandedRow}
        extraRow={extraRow}
        showDateFilter={false}
        additionalBody={additionalBody}
      />
      {/* <div className="flex gap-x-2 items-center mt-4">
        <label>Page Size:</label>
        <input className="w-20" type="number" value={pageSize} onChange={(e) => setPageSize(parseInt(e.target.value))} />
      </div> */}
    </>
  );
}
